import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

export namespace ControlLibrary {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface ControlHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }

    export interface ControlEntity {
        componentType: string;
        policyCat: string;
        policyCriticality: string;
        policyDesc: string;
        policyHistory: ControlHistoryItem[];
        policyID: string;
        policyIDstatus: "active" | "inactive" | "deleted";
        policyLocation: string;
        policyOwner: string;
        policyPublishInPrivacyControl: "Yes" | "No";
        policyRisk: string;
        policySubprocessor: "Yes" | "No";
        policyTeam: string;
        backupLocation: string;
        backupNeeded: "Yes" | "No";
        backupRetention: string;
        classification: string;
        customFields: any[];
        dataStorageLocations: string[];
        dataTypesShared: string[];
        dataTypesSharedString: string;
        dateAdded: Date;
        label: string;
        lastReviewDate: Date;
        legalBits: string;
        nextReviewDate: Date;
        portability: "Yes" | "No";
        securityReview: "Yes" | "No";
        serial: string;
        socCriticality: string;
        socSubserviceOrganisation: string;
        status: "Active" | "Decommissioned" | "Deleted";
        vendorManagementNeeded: "Yes" | "No";
        vendorWebsite: string;
    }

    export interface ControlLibraryResponse {
        statusCode?: number;
        message?: string;
        policies?: ControlEntity[];
        entry?: ControlEntity;
        error?: string | null;
    }

    export class ControlInstance {
        private data: ControlLibrary.ControlEntity;

        constructor(data: ControlLibrary.ControlEntity) {
            this.data = data;
        }

        // A getter to access the underlying data if needed
        public get entity(): ControlLibrary.ControlEntity {
            return this.data;
        }

        // An edit method that calls the static Edit function on the Control class
        public async edit(updates: Partial<ControlLibrary.ControlEntity>): Promise<ControlLibrary.ControlLibraryResponse | void> {
            return ControlLibrary.ControlItem.Edit(this.data.policyID, updates);
        }
    }

    export class ControlItem {

        /**
         * Add an policy to the API.
         * @param id - The ID of the policy to add.
         * @param policyData - Partial policy data to add.
         * @param callback - Optional callback after response.
         */
        static async Add(
            id: string,
            policyData: Partial<ControlEntity>,
            callback?: (res: ControlLibraryResponse) => any
        ): Promise<ControlLibraryResponse | void> {
            console.log("Calling API Add", { companyID: window.companyID, ...policyData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/soa/add/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...policyData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Delete an policy from the API.
         * @param id - The ID of the policy to delete.
         * @param callback - Optional callback after response.
         */
        static async Delete(
            id: string,
            callback?: (res: ControlLibraryResponse) => any
        ): Promise<ControlLibraryResponse | void> {
            console.log("Calling API Delete", { companyID: window.companyID });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/soa/delete/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }


        /**
         * Edit an existing policy in the API.
         * @param id - The ID of the policy to edit.
         * @param policyData - Partial policy data to update.
         * @param callback - Optional callback after response.
         */
        static async Edit(
            id: string,
            policyData: Partial<ControlEntity>,
            callback?: (res: ControlLibraryResponse) => any
        ): Promise<ControlLibraryResponse | void> {
            console.log("Calling API Edit", { companyID: window.companyID, ...policyData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/soa/edit/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...policyData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Fetch all policies from the API.
         */
        static async Get(): Promise<ControlLibrary.ControlEntity[] | undefined>;

        /**
         * Fetch a specific policy by ID from the API.
         * @param id - The ID of the policy to fetch.
         */
        static async Get(id: string | number): Promise<ControlInstance | undefined>;

        static async Get(id?: string | number): Promise<ControlLibrary.ControlEntity[] | ControlInstance | undefined> {
            if (typeof id === 'undefined' || id === 'all') {
                // Fetch all policies
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/soa/get/all`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.standards;
            } else {
                // Fetch single policy
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/soa/get/${id}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });

                if (res.entry) {
                    return new ControlInstance(res.entry);
                } else {
                    return undefined;
                }
            }
        }
    }

    export const Control = new Proxy(ControlItem, {
        get(target, prop) {
            const lowerCasedProp = prop.toString().toLowerCase();
            if (lowerCasedProp === 'get') {
                return target.Get;
            } else if (lowerCasedProp === 'add') {
                return target.Add;
            } else if (lowerCasedProp === 'delete') {
                return target.Delete;
            } else if (lowerCasedProp === 'edit') {
                return target.Edit;
            }

            return target[lowerCasedProp];
        }
    });
}